import {
  FailedDestinationEntityRecord,
  IDestinationEntityJobRunOptions,
  IGetRecordResponse,
  ITranslationMap,
  SourceEntityRecordStatus,
  type IDestinationEntity,
  type IDestinationEntityRecord,
  type IPracticeMigration,
} from '@principle-theorem/practice-migrations/interfaces';
import { User } from '@principle-theorem/principle-core';
import {
  IBrand,
  IOrganisation,
  IPractice,
  IStaffer,
  ServiceTypeModality,
} from '@principle-theorem/principle-core/interfaces';
import {
  WithPrimaryKey,
  snapshotCombineLatest,
  type WithRef,
} from '@principle-theorem/shared';
import { Observable, combineLatest, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { DestinationEntity } from '../../../destination/destination-entity';
import {
  BaseStafferDestinationEntity,
  IStafferMigrationData,
} from '../../../destination/entities/staff';
import { PracticeMigration } from '../../../practice-migrations';
import { TranslationMapHandler } from '../../../translation-map';
import {
  ExactStaffer,
  StafferSourceEntity,
  type IExactStaffer,
} from '../../source/entities/staff';
import {
  StaffToPracticeMapping,
  resolveExactStaffLocation,
} from '../mappings/practitioner-to-practice-mapping';
import { ExactStafferMappingHandler } from '../mappings/staff';
import { ExactStaffToUserMappingHandler } from '../mappings/staff-to-user';

export const STAFF_DESTINATION_ENTITY = DestinationEntity.init({
  metadata: {
    key: 'staff',
    label: 'Staff',
    description: `
      Exact exported data does not include any provider information except for the occasional 'Dr' title. Due to this, we cannot set any provider data for them.
    `,
  },
});

interface IStafferJobData {
  stafferRecord: IGetRecordResponse<ExactStaffer>;
  staff: WithPrimaryKey<ITranslationMap<IStaffer>>[];
  organisation: WithRef<IOrganisation>;
  brand: WithRef<IBrand>;
  staffToPractice: WithPrimaryKey<ITranslationMap<IPractice>>[];
}

export class StafferDestinationEntity extends BaseStafferDestinationEntity<ExactStaffer> {
  destinationEntity = STAFF_DESTINATION_ENTITY;
  stafferSourceEntity = new StafferSourceEntity();
  stafferCustomMapping = new ExactStafferMappingHandler();
  staffToUserCustomMapping = new ExactStaffToUserMappingHandler();

  override sourceEntities = {
    staff: new StafferSourceEntity(),
  };

  customMappings = {
    staff: new ExactStafferMappingHandler(),
    staffToUser: new ExactStaffToUserMappingHandler(),
    staffToPractice: new StaffToPracticeMapping(),
  };

  override buildJobData$(
    migration: WithRef<IPracticeMigration>,
    _destinationEntity: WithRef<IDestinationEntity>,
    translationMapHandler: TranslationMapHandler,
    runOptions: IDestinationEntityJobRunOptions
  ): Observable<IStafferJobData[]> {
    const staff$ = this.stafferCustomMapping.getRecords(translationMapHandler);
    const organisation$ = PracticeMigration.organisation$(migration);
    const brand$ = PracticeMigration.brand$(migration);
    const staffToPractice$ = this.customMappings.staffToPractice.getRecords(
      translationMapHandler
    );

    return snapshotCombineLatest([
      organisation$,
      brand$,
      staff$,
      staffToPractice$,
    ]).pipe(
      switchMap((dependencies) =>
        combineLatest([
          this.buildSourceRecordQuery$(
            migration,
            this.stafferSourceEntity,
            runOptions
          ),
          of(dependencies),
        ])
      ),
      map(([staffRecords, [organisation, brand, staff, staffToPractice]]) =>
        staffRecords.map((stafferRecord) => ({
          stafferRecord,
          organisation,
          brand,
          staff,
          staffToPractice,
        }))
      )
    );
  }

  async buildMigrationData(
    migration: WithRef<IPracticeMigration>,
    _destinationEntity: WithRef<IDestinationEntity>,
    translationMap: TranslationMapHandler,
    data: IStafferJobData
  ): Promise<
    | IStafferMigrationData
    | (IDestinationEntityRecord & FailedDestinationEntityRecord)
  > {
    if (data.stafferRecord.record.status === SourceEntityRecordStatus.Invalid) {
      return this._buildErrorResponse(
        data.stafferRecord.record,
        'Source staffer is invalid'
      );
    }

    const sourceStafferId = this.sourceEntities.staff.getSourceRecordId(
      data.stafferRecord.data
    );
    const practice = await resolveExactStaffLocation(
      sourceStafferId,
      translationMap,
      data.staffToPractice,
      migration.configuration.practices
    );
    if (!practice) {
      return this._buildErrorResponse(
        data.stafferRecord.record,
        'Staffer location could not be resolved'
      );
    }

    const user = User.init({
      name: this.getName(data.stafferRecord.data),
      email: data.stafferRecord.data.email,
      practices: [practice.ref],
      brands: [migration.configuration.brand.ref],
      isEnabled: false,
    });

    return {
      sourceStafferId,
      stafferDetails: {
        providerNumber: '',
        providerModality: ServiceTypeModality.GeneralDentist,
      },
      // Need to find out why we were using initials
      // sourceStafferInitials: this.getStafferInitials(
      //   data.stafferRecord.data
      // ),
      user,
    };
  }

  getName(data: IExactStaffer): string {
    const name = `${data.title ?? ''} ${data.first_name ?? ''} ${
      data.middle_name ?? ''
    } ${data.surname ?? ''}`;

    if (name.length === 0) {
      return data.id;
    }
    return name;
  }

  getStafferInitials(data: IExactStaffer): string {
    const firstInitial = data.first_name
      ? data.first_name[0].toUpperCase()
      : '';
    const middleInitial = data.middle_name
      ? data.middle_name[0].toUpperCase()
      : '';
    const lastInitial = data.surname ? data.surname[0].toUpperCase() : '';

    return `${firstInitial}${middleInitial}${lastInitial}`;
  }
}
